import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import Utils from "../../Utils";
import Swal from "sweetalert2";
import _ from "lodash";
import * as Marker from "@markerjs/markerjs3";
import jsPDF from "jspdf";
import moment from "moment";

const RecceTab = ({ mode, psId, canUpload, onUploadRecce }) => {
  const fileInputRef = useRef(null);
  const [downloadable, setDownloadable] = useState(false);
  const [recceImages, setRecceImages] = useState([]);
  const [recLoading, setRecLoading] = useState(false);
  const [openSetNumber, setOpenSetNumber] = useState(null);

  const title = mode === "recce" ? "Photoshoot Recce" : "Styling Deck";

  const apiUrl = process.env.REACT_APP_API_URL;

  const groupIntoSetsBySetNum = (items = []) => {
    const grouped = items.reduce((acc, item) => {
      const setNum = _.toNumber(item?.setnum);
      const normalizedSet = Number.isFinite(setNum) ? setNum : 0;

      if (!acc[normalizedSet]) {
        acc[normalizedSet] = [];
      }

      acc[normalizedSet].push({
        ...item,
      });

      return acc;
    }, {});

    return Object.keys(grouped)
      .map((setKey) => _.toNumber(setKey))
      .sort((a, b) => a - b)
      .flatMap((setKey) => grouped[setKey]);
  };

  /* ---------------- FETCH LIST ---------------- */
  const loadRecceImages = () => {
    axios
      .get(
        Utils.apiUrl(`photoshoot/recces/${psId}/${mode}`),
        Utils.apiHeaders(),
      )
      .then((res) => {
        if (res.data.success) {
          const processedData = groupIntoSetsBySetNum(res.data.data || []);

          setRecceImages(processedData);
          setDownloadable(res.data.downloadable);
          setOpenSetNumber(_.max(_.pick(processedData, "setnum")));
          if (
            processedData.some((item) => item.va_feedback) ||
            (canUpload() == 0 &&
              processedData.some((item) => item.status == "bt-processed"))
          ) {
            setDownloadable(true); //== if any image has feedback or approved, allow download
          }
        }
      })
      .catch(() => {});
  };

  useEffect(() => {
    loadRecceImages();
  }, [psId]);

  /* ---------------- FILE UPLOAD ---------------- */

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.type !== "application/pdf") {
      Swal.fire({
        icon: "warning",
        title: "Invalid file",
        text: "Only PDF files allowed",
      });
      return;
    }

    if (mode === "recce") {
      Swal.fire({
        title: title,
        text: `Are there any more ${title} submissions in upcoming days?`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes",
        cancelButtonText: "No",
      }).then((result) => {
        uploadFile(file, result.isConfirmed);
      });
    } else {
      uploadFile(file, true); // For styling deck, always allow more uploads
    }
  };

  const uploadFile = (file, moreUploads = false) => {
    setRecLoading(true);

    // ✅ ADD LOADER HERE (BEFORE API CALL)
    Swal.fire({
      title: "Uploading...",
      text: "Please wait",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const formData = new FormData();
    formData.append("pdffile", file);
    formData.append("moreUploads", moreUploads ? 1 : 0);

    axios
      .post(
        Utils.apiUrl(`photoshoot/reccepdf/${psId}/${mode}`),
        formData,
        Utils.apiHeaders(),
      )
      .then((res) => {
        setRecLoading(false);

        Swal.close(); // ✅ CLOSE LOADER FIRST

        if (res.data.success) {
          Swal.fire({
            icon: "success",
            title: "Uploaded!",
            text: res.data.message || "File uploaded successfully",
          });
          loadRecceImages();

          let newDets = { status: res.data.newStatus };
          newDets[`more_${mode}`] = moreUploads;

          onUploadRecce(newDets); // ✅ Update parent about new status
        } else {
          Swal.fire({
            icon: "error",
            title: "Upload Failed",
            text: res.data.message || "Something went wrong",
          });
        }
      })
      .catch((err) => {
        console.log(err);
        setRecLoading(false);

        Swal.close(); // ✅ CLOSE LOADER ON ERROR ALSO

        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to upload file",
        });
      });
  };

  const submitRecce = () => {
    Swal.fire({
      title: title,
      text: `There are no more ${title} submissions to be made for this photoshoot, are you sure you want to submit to Brand Team?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then((result) => {
      if (!result.isConfirmed) return;

      setRecLoading(true);

      // ✅ ADD LOADER HERE (BEFORE API CALL)
      Swal.fire({
        title: "Submitting...",
        text: "Please wait",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      axios
        .post(
          Utils.apiUrl(`photoshoot/reccesubmit/${psId}/${mode}`),
          { submit: true },
          Utils.apiHeaders(),
        )
        .then((res) => {
          setRecLoading(false);

          Swal.close(); // ✅ CLOSE LOADER FIRST

          if (res.data.success) {
            Swal.fire({
              icon: "success",
              title: "Submitted!",
              text: res.data.message || "Submission successful",
            });

            let newDets = { status: res.data.newStatus };
            newDets[`more_${mode}`] = false; // ✅ No more uploads allowed after submission

            onUploadRecce(newDets); // ✅ Update parent about new status

            loadRecceImages();
          } else {
            Swal.fire({
              icon: "error",
              title: "Submission Failed",
              text: res.data.message || "Something went wrong",
            });
          }
        })
        .catch((err) => {
          console.log(err);
          setRecLoading(false);

          Swal.close(); // ✅ CLOSE LOADER ON ERROR ALSO

          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to submit to Brand Team",
          });
        });
    });
  };

  /* ---------------- DELETE ---------------- */

  const deleteRecce = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This file will be permanently deleted!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        // Optional loader
        Swal.fire({
          title: "Deleting...",
          text: "Please wait",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        axios
          .delete(Utils.apiUrl(`photoshoot/recce/${id}`), Utils.apiHeaders())
          .then((res) => {
            Swal.close(); // close loader

            if (res.data.success) {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: res.data.message || "File deleted successfully",
                timer: 2000,
                showConfirmButton: false,
              });

              loadRecceImages();
            } else {
              Swal.fire({
                icon: "error",
                title: "Delete Failed",
                text: res.data.message || "Something went wrong",
              });
            }
          })
          .catch(() => {
            Swal.close();

            Swal.fire({
              icon: "error",
              title: "Error",
              text: "Failed to delete file",
            });
          });
      }
    });
  };

  /* ---------------- DRAG & DROP ---------------- */
  const stopEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    stopEvent(e);
    const file = e.dataTransfer.files?.[0];
    if (!file || file.type !== "application/pdf") return;

    fileInputRef.current.files = e.dataTransfer.files;
    fileInputRef.current.dispatchEvent(new Event("change", { bubbles: true }));
  };

  function preloadRecceImages() {
    const promises = recceImages
      /*.filter(
        (item) => item.status != "uploaded" && !_.isNull(item.va_feedback),
      )*/
      .map((item) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = "anonymous"; // Add this line
          img.src = `${process.env.REACT_APP_API_URL}/photoshoot/recce/full/${item.fname}`;

          img.onload = () => {
            const annotation = !_.isNull(item.va_feedback)
              ? JSON.parse(item.va_feedback)
              : null;
            resolve([img, annotation, item.filename]);
          };

          img.onerror = () => {
            console.warn("Failed to load:", img.src);
            resolve(null); // resolve to avoid blocking all
          };
        });
      });
    return Promise.all(promises);
  }

  function raterizeAll(imgs) {
    let markerAreas = [];

    const imageToDataUrl = (img) => {
      const canvas = document.createElement("canvas");
      const width = img.naturalWidth || img.width;
      const height = img.naturalHeight || img.height;

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0, width, height);
      }

      return [canvas.toDataURL("image/jpeg"), width, height];
    };

    const rasterPromises = imgs.map(([img, annotation, filename], index) => {
      return new Promise((resolve, reject) => {
        markerAreas[index] = new Marker.Renderer();
        markerAreas[index].targetImage = img;

        if (annotation) {
          markerAreas[index]
            .rasterize(annotation)
            .then((dataUrl) => {
              //console.log("Rasterized image ready:", dataUrl);
              resolve([dataUrl, img.width, img.height]);
            })
            .catch((err) => {
              console.log("Failed to rasterize:", err);
              resolve(imageToDataUrl(img));
            });
        } else {
          console.log("No annotation for image:", img.src);
          resolve(imageToDataUrl(img));
        }
      });
    });
    return Promise.all(rasterPromises);
  }

  const processPDF = () => {
    return preloadRecceImages()
      .then((imgs) => imgs.filter((img) => img !== null))
      .then(raterizeAll)
      .then(generatePDF)
      .catch((err) => {
        console.log("Failed to load or render images:", err);
      });
  };

  const generatePDF = (imgblobs) => {
    const pdf = new jsPDF();
    imgblobs.forEach(([imgData, imgWidth, imgHeight], index) => {
      if (index > 0) pdf.addPage();

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const margin = 10;
      const maxWidth = pageWidth - margin * 2;
      const maxHeight = pageHeight - margin * 2;

      const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
      imgWidth *= ratio;
      imgHeight *= ratio;

      const x = (pageWidth - imgWidth) / 2;
      const y = margin; // top aligned

      pdf.addImage(imgData, "JPEG", 10, 10, imgWidth, imgHeight); // A4 size with some margin
    });
    pdf.save(`photoshoot_${psId}_${mode}.pdf`);
  };

  const initiatePdf = () => {
    if (downloadable) {
      return axios
        .get(Utils.apiUrl(`photoshoot/downloadPdf/${mode}/${psId}`), {
          ...Utils.apiHeaders(),
          responseType: "blob", // important (your earlier code had it wrong place)
        })
        .then((res) => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `photoshoot_${psId}_${mode}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
        });
    } else {
      return processPDF();
    }
  };

  const downloadPdf = () => {
    Swal.fire({
      title: "Download PDF?",
      text: `Photoshoot_${psId}_${mode}.pdf will be downloaded`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, download",
    }).then((result) => {
      if (result.isConfirmed) {
        // 🔄 Show loader
        Swal.fire({
          title: "Downloading...",
          text: "Please wait while your PDF is being prepared",
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          },
        });

        initiatePdf()
          .then(() => {
            // ✅ Show success alert after download
            Swal.fire({
              icon: "success",
              title: "Downloaded!",
              text: "PDF downloaded successfully.",
              timer: 2000,
              showConfirmButton: false,
            });
          })
          .catch((err) => {
            console.error(err);

            // ERROR ALERT
            Swal.fire({
              icon: "error",
              title: "Download Failed",
              text: "Unable to download PDF. Please try again.",
            });
          });
      }
    });
  };

  const deleteAllRecces = (setNumber) => {
    Swal.fire({
      title: "Are you sure?",
      text: `All submitted ${title}s in set ${setNumber} will be permanently deleted!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete all!",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .delete(
            Utils.apiUrl(`photoshoot/recce/all/${psId}/${setNumber}`),
            Utils.apiHeaders(),
          )
          .then((res) => {
            if (res.data.success) {
              Swal.fire({
                icon: "success",
                title: "Deleted!",
                text: res.data.message || `All ${title}s deleted successfully`,
                timer: 2000,
                showConfirmButton: false,
              });
              loadRecceImages();
            } else {
              Swal.fire({
                icon: "error",
                title: "Delete Failed",
                text: res.data.message || "Something went wrong",
              });
            }
          });
      }
    });
  };

  const submitSetToBT = (setNumber) => {
    Swal.fire({
      title: "Are you sure?",
      text: `All submitted ${title}s in set ${setNumber} will be sent to the Brand Team for review!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#047857",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, send to Brand Team!",
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .put(
            Utils.apiUrl(`photoshoot/recce/statusUpdate`),
            { psId: psId, mode: mode, setnum: setNumber, status: "submitted" },
            Utils.apiHeaders(),
          )
          .then((res) => {
            if (res.data.success) {
              Swal.fire({
                icon: "success",
                title: "Sent!",
                text:
                  res.data.message ||
                  `All ${title}s sent to Brand Team successfully`,
                timer: 2000,
                showConfirmButton: false,
              });
              loadRecceImages();
            } else {
              Swal.fire({
                icon: "error",
                title: "Send Failed",
                text: res.data.message || "Something went wrong",
              });
            }
          });
      }
    });
  };

  const toggleSet = (setNumber) => {
    setOpenSetNumber((prev) => (prev === setNumber ? 0 : setNumber));
  };

  const visibleRecceItems = recceImages
    .reduce((acc, item, index) => {
      /*const shouldShow =
        (_.toNumber(canUpload()) > 0 && item.status == "uploaded") ||
        item.va_feedback;

      if (shouldShow) {*/
      acc.push({
        ...item,
        recordNumber: index + 1,
      });
      //}

      return acc;
    }, [])
    .sort((a, b) => {
      const setDiff = _.toNumber(b.setNum) - _.toNumber(a.setNum);
      if (setDiff !== 0) {
        return setDiff;
      }

      return _.toNumber(a.recordNumber) - _.toNumber(b.recordNumber);
    });

  const groupedBySet = visibleRecceItems.reduce((acc, item) => {
    const setNumber = _.toNumber(item.setnum);

    if (!acc[setNumber]) {
      acc[setNumber] = [];
    }

    acc[setNumber].push(item);
    return acc;
  }, {});

  const orderedSetNumbers = Object.keys(groupedBySet)
    .map((setNumber) => _.toNumber(setNumber))
    .sort((a, b) => b - a);

  useEffect(() => {
    if (orderedSetNumbers.length === 0) {
      if (openSetNumber !== null) {
        setOpenSetNumber(null);
      }
      return;
    }

    // First load: keep the first set accordion open.
    if (openSetNumber === null) {
      setOpenSetNumber(orderedSetNumbers[0]);
      return;
    }

    // Keep currently selected set if it still exists.
    if (openSetNumber !== 0 && !orderedSetNumbers.includes(openSetNumber)) {
      setOpenSetNumber(orderedSetNumbers[0]);
    }
  }, [orderedSetNumbers, openSetNumber]);

  /* ---------------- UI ---------------- */
  return (
    <div id={`tab-${mode}`} className={`tab-panel active`}>
      {mode === "recce" && (
        <div className="instruction-note mt-4 mb-4">
          <i className="fas fa-lightbulb mr-2"></i>
          Required only for New Build and Conversion Hotel.
        </div>
      )}

      {recLoading && (
        <div className="text-blue-500 font-semibold py-4">Uploading…</div>
      )}

      {recLoading === false && (
        <>
          {_.toNumber(canUpload()) > 0 && (
            <div
              className="drop-zone cursor-pointer"
              onClick={() => fileInputRef.current.click()}
              onDrop={handleDrop}
              onDragOver={stopEvent}
              onDragLeave={stopEvent}
            >
              <i className="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"></i>
              <p>Drag & Drop PDF or click to upload</p>
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                className="hidden"
                onChange={handleFileChange}
              />
            </div>
          )}
          {downloadable && (
            <div className="bg-blue-100 rounded p-1 mt-4 mb-4 text-xl">
              <i className="fas fa-lightbulb mr-2"></i>
              You can download Brand Team feedback as{" "}
              <button
                className="btn rounded-md bg-yellow-100 p-1 pr-2"
                onClick={downloadPdf}
              >
                <i className="fas fa-download mx-1"></i>
                PDF
              </button>
            </div>
          )}
          {_.toNumber(canUpload()) > 0 && (
            <div className="flex justify-end">
              {recceImages.length > 0 && (
                <button
                  className="rounded-md p-1 pr-2 bg-blue-600 text-white mx-3 mt-4"
                  onClick={submitRecce}
                >
                  Finalize {title} submission
                </button>
              )}
            </div>
          )}
          {recceImages.length > 0 &&
            _.toNumber(canUpload()) === 0 &&
            recceImages.filter((item) => item.status !== "submitted").length ===
              0 && (
              <div className="instruction-note mt-4 mb-4">
                <i className="fas fa-lightbulb mr-2"></i>
                All {title} submissions are finalized. Please wait for Brand
                Team feedback.
              </div>
            )}
          <div className="mt-8 space-y-4">
            {orderedSetNumbers.map((setNumber) => {
              const setItems = groupedBySet[setNumber] || [];
              const isOpen = openSetNumber === setItems[0]?.setnum; // open the set if its setnum matches the openSetNumber

              return (
                <div
                  key={`set-${setNumber}`}
                  className={`rounded-lg border border-gray-200 bg-white shadow-sm ${Utils.getSetColors(setNumber)}`}
                >
                  <button
                    type="button"
                    className="w-full px-4 py-3 flex items-center justify-between text-left cursor-pointer"
                    onClick={() => toggleSet(setNumber)}
                    aria-expanded={isOpen}
                    aria-controls={`set-panel-${setNumber}`}
                  >
                    <span className="font-semibold text-gray-800">
                      Set {setNumber} (
                      {setItems[0].status === "bt-completed" && (
                        <span>
                          Brand Team has commented on the following images with
                          <span className="bg-green-700 text-white p-1 rounded text-sm  ml-1">
                            <i className="fa-solid fa-magnifying-glass"></i>
                          </span>
                        </span>
                      )}
                      {setItems[0].status !== "bt-completed" && (
                        <span>Pending Brand Team review</span>
                      )}
                      ) - {moment(setItems[0]?.created_at).format("DD MMM 'YY")}
                    </span>
                    <i
                      className={`fa-solid ${
                        isOpen ? "fa-chevron-up" : "fa-chevron-down"
                      } text-gray-600`}
                    ></i>
                  </button>

                  {isOpen && (
                    <div
                      id={`set-panel-${setNumber}`}
                      className="px-4 pb-4 mt-4"
                    >
                      {!setItems.find((st) => st.status !== "uploaded") && (
                        <div className="flex justify-end mb-4 gap-2">
                          <button
                            className="rounded-md p-1 px-4 bg-green-700 text-white"
                            onClick={() => submitSetToBT(setItems[0].setnum)}
                          >
                            Send this set to Brand Team for Review
                          </button>
                          <button
                            className="rounded-md p-1 px-4 bg-red-700 text-white"
                            onClick={() => deleteAllRecces(setItems[0].setnum)}
                          >
                            Delete all submissions of this Set
                          </button>
                        </div>
                      )}

                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
                        {setItems.map((item) => (
                          <div
                            key={item.id}
                            className="relative bg-white rounded-lg shadow border"
                          >
                            <div className="h-40 bg-gray-100 flex items-center justify-center">
                              <img
                                src={`${apiUrl}/photoshoot/recce/thumb/${item.fname}`}
                                className="object-cover w-full h-full"
                                alt=""
                              />
                            </div>

                            <div
                              className={`p-3 text-sm font-semibold truncate`}
                            >
                              Pic {item.recordNumber}
                              {false && item.ai_feedback && (
                                <div className="text-xs font-normal">
                                  {item.ai_feedback}
                                </div>
                              )}
                            </div>

                            {!setItems.find(
                              (st) => st.status !== "uploaded",
                            ) && (
                              <button
                                className="absolute top-2 right-2 text-red-700 bg-red-100 p-1 rounded text-sm"
                                onClick={() => deleteRecce(item.id)}
                              >
                                <i className="fa-solid fa-xl fa-times-circle"></i>
                              </button>
                            )}

                            {item.status == "bt-completed" &&
                              item.va_feedback && (
                                <a
                                  className="absolute top-2 right-2 bg-green-700 text-white p-1 rounded text-sm"
                                  href={`${process.env.PUBLIC_URL}/photoshoot-recce/${psId}/${mode}/${item.fname}`}
                                >
                                  <i className="fa-solid fa-magnifying-glass"></i>
                                </a>
                              )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default RecceTab;
